import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface PasswordStatusResponse {
  passwordDt: string;
  lastPasswordNotification: string | null;
  showPasswordExpiryNotificationDays: string;
}

@Injectable({
  providedIn: 'root'
})
export class PasswordNotificationService {
  private readonly httpClient = inject(HttpClient);

  getPasswordStatus(dealerId: string, login: string): Observable<PasswordStatusResponse | null> {
    // Password endpoints are served from backOfficeUrl (not backOfficeUrl) — auth service boundary.
    const url = `${environment.backOfficeUrl}${environment.serviceUris.passwordStatus
      .replace('{dealerId}', dealerId)
      .replace('{login}', login)}`;
    return this.httpClient.get<PasswordStatusResponse>(url).pipe(catchError(() => of(null)));
  }

  putPasswordNotification(dealerId: string, login: string): void {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.passwordNotification
      .replace('{dealerId}', dealerId)
      .replace('{login}', login)}`;
    this.httpClient
      .put(url, null)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  daysUntilExpiry(passwordDt: string): number {
    if (!passwordDt) return -1;
    const expiry = new Date(passwordDt);
    if (isNaN(expiry.getTime())) return -1;
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const expiryLocal = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
    const diffMs = expiryLocal.getTime() - todayLocal.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  shouldShowAlert(status: PasswordStatusResponse | null): status is PasswordStatusResponse {
    if (!status) return false;
    const days = this.daysUntilExpiry(status.passwordDt);
    if (days < 0 || days > parseInt(status.showPasswordExpiryNotificationDays, 10)) return false;
    if (status.lastPasswordNotification === null) return true;
    const lastNotifDate = new Date(status.lastPasswordNotification);
    const today = new Date();
    return lastNotifDate.toISOString().slice(0, 10) !== today.toISOString().slice(0, 10);
  }
}
